/**
 * سه فیلدِ ساخت‌یافته‌ی صفحه‌ی جزئیات: کِی، کجا، چند.
 *
 * قبلاً هر کلاینت خودش این‌ها را دورِ توضیحات پخش می‌کرد — وب سه پاراگرافِ
 * بی‌برچسب، موبایل دو `IconText` و یک چیپ — و نتیجه این بود که یک آیتم در دو
 * جا دو چیزِ متفاوت می‌گفت (مثلاً تخفیف فقط روی موبایل دیده می‌شد). این‌جا
 * یک‌بار ساخته می‌شود و هر دو صفحه همان فهرست را رندر می‌کنند.
 *
 * خروجی همیشه فقط ردیف‌هایی است که واقعاً داده دارند: فیلدِ خالی اصلاً ردیف
 * نمی‌سازد — «نامشخص» نوشتن جا می‌گیرد و چیزی به خواننده نمی‌گوید.
 *
 * ترجمه همین‌جا انجام می‌شود (`t`)، پس باید موقعِ رندر صدا زده شود نه بالای
 * ماژول؛ وگرنه با عوض شدنِ زبان به‌روز نمی‌شود.
 */
import type { FieldAlternatives } from '../domain/types.ts';
import {
  formatDateShort, formatTime, formatWeekdayDate, isSameDay, monthlyLabel, weekdayLabel,
} from './dates.ts';
import { altLinesFor, type AltLine } from './fieldAlternatives.ts';
import { t } from './i18n.ts';

export type ItemFieldName = 'when' | 'where' | 'price';

/** اسمِ آیکون — هر دو کلاینت این سه تا را دارند (`ui/icons.tsx` در هر اپ). */
export type ItemFieldIcon = 'clock' | 'pin' | 'tag';

export interface ItemField {
  name: ItemFieldName;
  icon: ItemFieldIcon;
  /** برچسبِ ترجمه‌شده — «When» / «کِی» */
  label: string;
  value: string;
  /**
   * «منبعِ دیگری چیزِ دیگری می‌گوید» برای همین ردیف — معمولاً خالی.
   * نگاه کن به lib/fieldAlternatives.ts.
   */
  alternatives: AltLine[];
}

/**
 * چیزی که برای ساختنِ سه فیلد لازم است. عمداً ساختاری و همه‌اش اختیاری، چون
 * صفحه‌ی جزئیات هم `EventItem` می‌گیرد هم `Business` و آن دو فیلدهای
 * متفاوتی دارند (مایگریشن 0042 جدولشان را یکی کرد، نه تایپشان را).
 */
export interface ItemFieldSource {
  startsAt?: string | null;
  endsAt?: string | null;
  weekdays?: number[] | null;
  monthdays?: number[] | null;
  monthWeeks?: number[] | null;
  venueName?: string | null;
  address?: string | null;
  city?: string | null;
  isFree?: boolean;
  priceNote?: string | null;
  hasDiscount?: boolean;
  discountNote?: string | null;
  /** نسخه‌های دیگری که منابع برای همین فیلدها گفته‌اند. مکان‌ها هرگز ندارند. */
  alternatives?: FieldAlternatives | null;
}

/** اولین ورودی‌ای که واقعاً متن است؛ `null`، `undefined` و فاصله‌ی خالی رد می‌شوند. */
const firstText = (...xs: (string | null | undefined)[]): string =>
  xs.find((x) => !!x && x.trim() !== '')?.trim() ?? '';

const isMidnight = (iso: string): boolean => {
  const d = new Date(iso);
  return d.getHours() === 0 && d.getMinutes() === 0;
};

/**
 * ساعتِ یک رخداد: «۲۰:۰۰» یا «۲۰:۰۰ – ۲۳:۰۰». خالی یعنی ساعتی نداریم.
 *
 * نیمه‌شبِ دقیق ساعت حساب نمی‌شود: رخدادهای کرال‌شده‌ای که منبعشان فقط تاریخ
 * داده روی نیمه‌شب می‌نشینند، و «۰۰:۰۰» نوشتن برایشان اطلاعات نیست، ادعای
 * غلط است. رخدادی که واقعاً نیمه‌شب شروع شود قربانیِ همین قاعده می‌شود —
 * تا وقتی رخدادها پرچمِ `dateOnly` (مثلِ سیگنال) نداشته باشند، مرزِ بهتری نیست.
 */
const timeValue = (e: ItemFieldSource): string => {
  if (!e.startsAt) return '';
  const endMidnight = !e.endsAt || isMidnight(e.endsAt);
  if (isMidnight(e.startsAt) && endMidnight) return '';
  const from = formatTime(e.startsAt);
  return e.endsAt ? `${from} – ${formatTime(e.endsAt)}` : from;
};

/**
 * «کِی» برای یک رخداد.
 *
 * برخلافِ کارتِ فشرده‌ی کشف (`eventDateLabel`)، این‌جا تاریخِ شروع حتی بعد از
 * شروعِ رخداد هم مخفی نمی‌شود: کسی که تا صفحه‌ی جزئیات آمده جزئیاتِ کامل
 * می‌خواهد، نه فهرستِ کارهای مانده. ساعت هم به همین دلیل این‌جا هست و روی
 * کارت نیست: «کِی» بدونِ ساعت به دردِ برنامه‌ریزیِ رفتن نمی‌خورد.
 */
const whenValue = (e: ItemFieldSource): string => {
  // الگوی ماهانه‌ی هفته‌محور («اولین جمعه‌ی هر ماه») خودش روزِ هفته را در خود
  // دارد، پس `weekdays` دوباره نوشته نمی‌شود — همان قاعده‌ی eventDateLabel.
  const nthWeek = !!e.monthWeeks?.length && !!e.weekdays?.length;
  // پایانِ هم‌روزِ شروع فقط ساعتش حرفِ تازه دارد («۵ مرداد → ۵ مرداد» هیچی
  // نمی‌گوید)، پس تاریخش حذف می‌شود و ساعتش در timeValue می‌آید.
  const oneDay = isSameDay(e.startsAt, e.endsAt);
  // پایان با فاصله به شروع می‌چسبد نه با « · »: «۱۴ آگوست · → ۱۶ آگوست» دو
  // جداکننده‌ی پشتِ سرِ هم بود و مثلِ یه غلطِ تایپی خونده می‌شد.
  const dateText = [
    formatWeekdayDate(e.startsAt),
    e.endsAt && !oneDay ? `→ ${formatDateShort(e.endsAt)}` : '',
  ].filter(Boolean).join(' ');

  return [dateText, timeValue(e), nthWeek ? '' : weekdayLabel(e.weekdays), monthlyLabel(e)]
    .filter(Boolean).join(' · ');
};

/**
 * «چند». رایگان و تخفیف هر دو ممکن است کنارِ هم بیایند: یک رخدادِ رایگان
 * می‌تواند نوشته‌ی قیمتی هم داشته باشد، و یک مکانِ تخفیف‌دار نوشته‌ی تخفیف.
 */
const priceValue = (e: ItemFieldSource): string => [
  e.isFree ? t('free') : firstText(e.priceNote),
  e.hasDiscount ? firstText(e.discountNote, t('hasDiscount')) : '',
].filter(Boolean).join(' · ');

/**
 * سه فیلد، به ترتیبِ ثابتِ کِی → کجا → چند. ردیفِ بی‌داده حذف می‌شود، پس
 * خروجی ممکن است صفر تا سه عضو داشته باشد.
 *
 * `kind` از خودِ صفحه می‌آید نه از داده: یک مکان تاریخ ندارد و «کِی»اش همیشه
 * همان «یه جای همیشگیه» است.
 *
 * ردیفی که مقدار ندارد حذف می‌شود، حتی اگر جایگزین داشته باشد: «منبعِ دیگری
 * ۲۰:۰۰ می‌گوید» بدونِ ساعتی که بالایش نوشته شده باشد، مقایسه نیست — فقط یک
 * عددِ معلق است.
 */
export const itemFields = (e: ItemFieldSource, kind: 'event' | 'place'): ItemField[] => ([
  {
    name: 'when' as const,
    icon: 'clock' as const,
    label: t('fieldWhen'),
    value: kind === 'event' ? whenValue(e) : t('placeAlwaysOpen'),
    alternatives: kind === 'event' ? altLinesFor(e.alternatives, 'when') : [],
  },
  {
    name: 'where' as const,
    icon: 'pin' as const,
    label: t('fieldWhere'),
    alternatives: altLinesFor(e.alternatives, 'where'),
    // صفحه‌ی جزئیات جای جوابِ کامل است: سالن **و** نشانی با هم — «The
    // Bronson» تنها، خواننده را تا نقشه هم نمی‌رساند. کارتِ کشف این تابع را
    // مصرف نمی‌کند (cards خودشان venueName را می‌خوانند)، پس آن‌جا همچنان
    // فقط اسمِ سالن است. شهر آخرین پناه است، چون «مونترال» از هیچی بهتر است.
    value: [firstText(e.venueName), firstText(e.address)].filter(Boolean).join(' · ')
      || firstText(e.city),
  },
  {
    name: 'price' as const,
    icon: 'tag' as const,
    label: t('fieldPrice'),
    value: priceValue(e),
    alternatives: altLinesFor(e.alternatives, 'price'),
  },
]).filter((f) => f.value !== '');
